import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { PAGE_META } from '@/data/pageMeta';
import usePageMeta from '@/hooks/usePageMeta';
import shared from '@/styles/public.module.css';
import formStyles from '@/styles/form.module.css';
import shell from '@/styles/authShell.module.css';
import AuthSplitLayout from '@/components/auth/AuthSplitLayout';
import { LOGIN_PANEL } from '@/data/authPanels';
import { rememberOauthState } from '@/lib/googleOauthState';
import { roleHome } from '@/lib/roleHome';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  usePageMeta(PAGE_META.login);

  const navigate = useNavigate();
  const location = useLocation();
  const referralPrefill = (() => {
    try { return JSON.parse(sessionStorage.getItem('referral_prefill') || '{}'); }
    catch { return {}; }
  })();
  const referralToken = (typeof window !== 'undefined') ? sessionStorage.getItem('referral_token') : null;
  const fromReferral = !!referralToken;
  const [formData, setFormData] = useState({ email: referralPrefill.email || '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Show a one-time info banner to funders & lawyers right after signup, telling them
  // their account request requires email verification + staff review before they can log in.
  const [showApprovalPending, setShowApprovalPending] = useState(() => {
    if (location.state?.pendingApprovalRole) return true;
    try { return !!sessionStorage.getItem('pending_approval_signup'); } catch { return false; }
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectPath = params.get('redirect');
    if (redirectPath) {
      sessionStorage.setItem('login_redirect', redirectPath);
    }
    // Prefill email if provided via query string (e.g., from approval emails)
    const emailFromQuery = params.get('email');
    if (emailFromQuery) {
      setFormData((prev) => ({ ...prev, email: emailFromQuery }));
    }
    // Email verification deep-link: show a one-time toast + (for funder/lawyer)
    // refresh the pending-approval banner so they know what happens next
    if (params.get('verified') === '1') {
      const role = params.get('role');
      if (role === 'funder' || role === 'lawyer') {
        try { sessionStorage.setItem('pending_approval_signup', role); } catch { /* ignore */ }
        setShowApprovalPending(true);
        // Defer toast slightly so Sonner's subscriber is ready after mount
        setTimeout(() => {
          toast.success('Email verified — your account request is now under review by our team.', { duration: 7000 });
        }, 100);
      } else {
        setTimeout(() => {
          toast.success('Email verified! You can now sign in.', { duration: 5000 });
        }, 100);
      }
      // Scrub the verified flag from the URL so a refresh doesn't re-fire the toast
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('verified');
        url.searchParams.delete('role');
        window.history.replaceState({}, '', url.pathname + (url.search ? url.search : ''));
      } catch { /* ignore */ }
    }
  }, []);

  const getPostLoginRoute = (role) => {
    const saved = sessionStorage.getItem('login_redirect');
    if (saved) {
      sessionStorage.removeItem('login_redirect');
      return saved;
    }
    return roleHome(role, '/client');
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: formData.email, password: formData.password })
      });

      if (!response.ok) {
        // Prefer the backend's own reason when it gave a specific one. It
        // returns 401 with a deliberately generic detail for a bad credential,
        // and 400/429 with a safe, specific detail for everything else — no
        // password set, account disabled, rate limited.
        //
        // check-verification below only knows "exists" and "verified", so it
        // reports an account that never had a password as merely unverified.
        // That sent placeholder attorneys and admin-provisioned users chasing
        // a verification email nobody sent, and hid the one control that
        // works: Forgot password.
        if (response.status !== 401) {
          let detail = '';
          try { detail = (await response.json())?.detail || ''; } catch { /* non-JSON error body */ }
          if (detail) {
            toast.error(detail);
            return;
          }
        }

        const checkResponse = await fetch(`${API}/auth/check-verification?email=${encodeURIComponent(formData.email)}`);
        if (checkResponse.ok) {
          const checkData = await checkResponse.json();
          if (checkData.exists && !checkData.verified) {
            toast.error('Please verify your email before logging in. Check your inbox for the verification link.');
          } else if (!checkData.exists) {
            toast.error('No account found with this email.');
          } else {
            toast.error('Invalid email or password.');
          }
        } else {
          toast.error('Login failed. Please check your credentials.');
        }
        return;
      }

      let data = {};
      try { data = await response.json(); } catch (e) { console.warn('Login response not JSON:', e); }

      if (data.access_token) localStorage.setItem('access_token', data.access_token);
      // Clear the pending-approval banner flag once the user successfully signs in
      try { sessionStorage.removeItem('pending_approval_signup'); } catch { /* ignore */ }
      
      // Auto-claim referral if token exists in sessionStorage
      const referralTokenFromStorage = sessionStorage.getItem('referral_token');
      if (referralTokenFromStorage && data.user?.role === 'client') {
        try {
          // Snapshot referral context for the post-link welcome toast
          let refPrefill = {};
          try { refPrefill = JSON.parse(sessionStorage.getItem('referral_prefill') || '{}'); } catch { /* ignore */ }
          const claimResp = await fetch(`${API}/referral/${referralTokenFromStorage}/claim`, {
            method: 'POST', credentials: 'include',
            headers: { 'Authorization': `Bearer ${data.access_token}` }
          });
          let claimData = {};
          try { claimData = await claimResp.json(); } catch { /* ignore */ }
          sessionStorage.removeItem('referral_token');
          sessionStorage.removeItem('referral_prefill');
          if (claimResp.ok && claimData.application_id) {
            sessionStorage.setItem('referral_welcome', JSON.stringify({
              applicationId: claimData.application_id,
              attorneyName: refPrefill.lawyerName || '',
              lawFirmName: refPrefill.lawFirmName || '',
              firstName: refPrefill.firstName || data.user?.name?.split(' ')[0] || ''
            }));
          }
          navigate('/client');
          return;
        } catch (err) { console.error('Referral claim failed:', err); }
      }
      
      toast.success('Login successful!');
      navigate(getPostLoginRoute(data.user?.role));
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + '/auth/google/callback';
    // Preserve referral_token across the OAuth redirect (sessionStorage persists in the same tab)
    try {
      const params = new URLSearchParams(window.location.search);
      const tokenFromUrl = params.get('referral_token');
      if (tokenFromUrl) sessionStorage.setItem('referral_token', tokenFromUrl);
    } catch (e) { /* ignore */ }
    try {
      const response = await fetch(`${API}/auth/google/login?redirect_uri=${encodeURIComponent(redirectUri)}`);
      const data = await response.json();
      if (data.auth_url) {
        // Hold the CSRF state so GoogleCallback can check what Google returns.
        rememberOauthState(data.state);
        window.location.href = data.auth_url;
      } else {
        toast.error('Failed to initiate Google login');
      }
    } catch (error) {
      toast.error('Failed to connect to Google');
    }
  };

  /* ---------- Main login page ---------- */
  return (
    <AuthSplitLayout
      testId="login-page"
      {...LOGIN_PANEL}
      topRight={
        <>
          <span>New here?</span>
          <button
            type="button"
            className={`${shared.btn} ${shared.btnQuiet}`}
            onClick={() => navigate('/signup')}
            data-testid="signup-link"
          >
            Create an account
          </button>
        </>
      }
    >
      <div data-testid="login-card">
        <div className={shell.headStart}>
          <h1 className={shell.title}>Welcome back</h1>
          <p className={shell.subtitle}>Sign in to see your offers and case status.</p>
        </div>

        {showApprovalPending && (
          <div className={shell.notice} data-testid="approval-pending-banner">
            <ShieldCheck size={20} aria-hidden="true" />
            <div>
              <strong>Your account request has been received.</strong>
              <p>
                Please check your email to verify your email address and activate your
                account request.
              </p>
              <p>
                Once verified, our team will review your application, and you&apos;ll
                receive a follow-up email with your account approval status and login
                instructions.
              </p>
            </div>
            <button
              type="button"
              aria-label="Dismiss"
              data-testid="approval-pending-dismiss"
              className={shell.noticeDismiss}
              onClick={() => {
                setShowApprovalPending(false);
                try { sessionStorage.removeItem('pending_approval_signup'); } catch { /* ignore */ }
              }}
            >
              <X size={14} />
            </button>
          </div>
        )}

        {fromReferral && (
          <div className={shell.banner} data-testid="referral-banner">
            <strong>Welcome back!</strong> {referralPrefill.lawyerName ? `${referralPrefill.lawyerName} ` : 'Your attorney '}
            {referralPrefill.lawFirmName ? `at ${referralPrefill.lawFirmName} ` : ''}
            referred you. Sign in to view your application.
          </div>
        )}

        <button
          type="button"
          className={`${shared.btn} ${shared.btnQuiet} ${shell.fullBtn}`}
          onClick={handleGoogleLogin}
          disabled={loading}
          data-testid="google-login-button"
        >
          <svg className={shell.googleIcon} viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Continue with Google
        </button>

        <div className={shell.divider}>or sign in with email</div>

        <form onSubmit={handleEmailLogin} data-testid="email-login-form">
          <div className={formStyles.field}>
            <label htmlFor="email" className={formStyles.label}>Email Address</label>
            <div className={formStyles.adorned}>
              <Mail size={17} className={formStyles.adornedIcon} aria-hidden="true" />
              <input
                id="email"
                type="email"
                className={`${formStyles.control} ${formStyles.withIcon}`}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@example.com"
                required
                data-testid="input-email"
              />
            </div>
          </div>

          <div className={formStyles.field}>
            <div className={shell.labelRow}>
              <label htmlFor="password" className={formStyles.label}>Password</label>
              <button
                type="button"
                className={shell.linkSmall}
                onClick={() => navigate('/forgot-password')}
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            </div>
            <div className={formStyles.adorned}>
              <Lock size={17} className={formStyles.adornedIcon} aria-hidden="true" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className={`${formStyles.control} ${formStyles.withIcon}`}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter your password"
                required
                data-testid="input-password"
              />
              <button
                type="button"
                className={formStyles.passwordToggle}
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className={`${shared.btn} ${shared.btnAction} ${shell.fullBtn} ${shell.spacedTop}`}
            disabled={loading}
            data-testid="submit-login-button"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {/* New tab, matching the signup wizard: reading these should not
            abandon a half-completed sign-in, and someone arriving from an
            email link would lose the redirect held in sessionStorage. */}
        <p className={shell.footNote}>
          By continuing, you agree to our{' '}
          <a
            href="/terms"
            className={shell.link}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Terms of Service (opens in a new tab)"
          >
            Terms of Service
          </a>{' '}and{' '}
          <a
            href="/privacy"
            className={shell.link}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Privacy Policy (opens in a new tab)"
          >
            Privacy Policy
          </a>.
        </p>
      </div>
    </AuthSplitLayout>
  );
};

export default Login;
